import { useEffect, useState } from 'react';
import TileCard from '../TileCard';
import { getCachedResponse, getServiceBusSummary, prefetch } from '../../api/client';
import type { Env, ServiceBusSummary } from '../../types';

const HELP =
  'Service Bus namespace health: active/DLQ/scheduled message counts across queues, ' +
  'topics and their subscriptions. DLQ > 0 usually means a downstream integration is ' +
  'silently failing — check the workflow breakdown and AI exceptions tiles next. ' +
  'Top DLQ sources list highlights the worst offenders.';

interface Props {
  env: Env;
  compact?: boolean;
  detailsHref?: string;
  refreshToken?: number;
}

export default function ServiceBusSummaryTile({ env, compact = false, detailsHref, refreshToken = 0 }: Props) {
  const [data, setData] = useState<ServiceBusSummary | null>(() =>
    getCachedResponse<ServiceBusSummary>('service-bus-summary', { env }),
  );
  const [loading, setLoading] = useState(data === null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const cached = getCachedResponse<ServiceBusSummary>('service-bus-summary', { env });
    if (cached) setData(cached);
    setLoading(cached === null);
    setError(null);
    getServiceBusSummary(env)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    // SB counts are a real-time snapshot — no separate detail endpoint to warm
    // up, but rehitting here from compact mode keeps localStorage fresh.
    if (compact) prefetch('service-bus-summary', { env });
    return () => {
      cancelled = true;
    };
  }, [env, refreshToken, compact]);

  const generatedFooter = data ? (
    <span className="tile-footer-row">
      <span>
        As of {new Date(data.generatedAt).toLocaleTimeString()}
        {data.namespace && ` · ${data.namespace}`}
      </span>
      {detailsHref && (
        <a href={`#${detailsHref}`} className="tile-details-link" target="_blank" rel="noopener noreferrer">
          View details →
        </a>
      )}
    </span>
  ) : undefined;

  const totalDlq = data?.totalDlq ?? 0;

  return (
    <TileCard
      title={`Service Bus — ${env.toUpperCase()}`}
      help={HELP}
      hideHelp={compact}
      loading={loading}
      error={error}
      footer={generatedFooter}
    >
      {data?.status === 'not-configured' && <p className="tile-empty">{data.message}</p>}
      {data?.status === 'error' && <div className="tile-error">Query failed: {data.message}</div>}
      {data?.status === 'ok' && (
        <>
          <dl className="stats-grid">
            <div>
              <dt>Active</dt>
              <dd>{data.totalActive ?? 0}</dd>
            </div>
            <div className={totalDlq > 0 ? 'bad' : 'ok'}>
              <dt>DLQ</dt>
              <dd>{totalDlq}</dd>
            </div>
            <div>
              <dt>Scheduled</dt>
              <dd>{data.totalScheduled ?? 0}</dd>
            </div>
            <div>
              <dt>Entities</dt>
              <dd>{data.rows?.length ?? 0}</dd>
            </div>
          </dl>
          {!compact && (
          <div className="ai-detail-grid">
            <div>
              <h3 className="ai-detail-title">Top DLQ sources</h3>
              {(data.topDlqSources?.length ?? 0) === 0 ? (
                <p className="tile-empty">None — no messages dead-lettered</p>
              ) : (
                <ul className="ai-list">
                  {data.topDlqSources!.map((s) => (
                    <li key={s.name}>
                      <strong>{s.name}</strong>
                      <span className="muted"> × {s.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <h3 className="ai-detail-title">Entities with activity</h3>
              {(data.rows?.filter((r) => r.kind !== 'topic' && (r.active > 0 || r.dlq > 0)).length ?? 0) === 0 ? (
                <p className="tile-empty">All idle</p>
              ) : (
                <ul className="ai-list">
                  {data
                    .rows!.filter((r) => r.kind !== 'topic' && (r.active > 0 || r.dlq > 0))
                    .slice(0, 8)
                    .map((r, i) => (
                      <li key={`${r.topic}-${r.name}-${i}`}>
                        <strong>{r.topic ? `${r.topic}/${r.name}` : r.name}</strong>
                        <span className="muted">
                          {' '}· active {r.active}
                          {r.dlq > 0 && <span className="text-bad"> · dlq {r.dlq}</span>}
                        </span>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          </div>
          )}
        </>
      )}
    </TileCard>
  );
}
